import {
	keyBy,
	find
} from "lodash";
import {
	CONDITION_OPERATORS,
	ENGINE_TYPES,
	ENGINE_EVALUATIONS,
	TRIGGER_OPERATOR_CODES
} from "../screens/rule-provider/constants.js";

class RuleValidator {
	constructor($logger, ruleConfigService) {
		this.$logger = $logger;
		this.ruleConfigService = ruleConfigService;
	}

	checkRuleForPresenceOfChildrenOrEngine = (rule) => {
		if(
			(!!rule.Conditions && !!rule.Children)
			|| (!!rule.Engine && !!rule.Children && !!rule.Conditions)
		) {
			this.$logger.error(`Rule Id ${rule.Id} has an invalid combination of Engine, Conditions, and Children.  Removing rule.`, {
				rule
			});
			return false;
		}
		else if(!rule.Engine && !rule.Conditions && !rule.Children) {
			this.$logger.error(`Rule Id ${rule.Id} has none of Engine, Conditions, nor Children.  Removing rule.`, {
				rule
			});
			return false;
		}
		else if(!rule.Children && (!!rule.Engine !== !!rule.Conditions?.length)) {
			this.$logger.error(`Rule Id ${rule.Id} has an engine without conditions or conditions without an engine.  Removing rule.`, {
				rule,
				children: !!rule.Children,
				engine: !!rule.Engine,
				conditions: !!rule.Conditions?.length
			});
			return false;
		}
		return true;
	};

	checkRuleForValidEngineAndEvaluation = (rule) => {
		if(!!rule.Children) {
			return true;
		}
		if(!rule.Engine || !ENGINE_TYPES[rule.Engine.Code]) {
			this.$logger.error(`Rule Id ${rule.Id} uses unimplemented Engine ${rule.Engine.Code}.  Removing rule.`, { rule });
			return false;
		}
		
		const engineFuncName = ENGINE_TYPES[rule.Engine.Code].function;
		const validConditions = rule.Conditions.every(c => {
			const thisConditionOperator = c.Operator?.Code ? CONDITION_OPERATORS[c.Operator?.Code] : "evaluate";
			return !!ENGINE_EVALUATIONS[engineFuncName]?.[thisConditionOperator];
		});
		
		if(!validConditions) {
			this.$logger.error(`Rule Id ${rule.Id} uses an invalid engine/condition combination.  Removing rule.`, { rule });
			return false;
		}

		return true;
	};

	checkRuleForValidFields = (fields) => (rule) => {
		const evaluatees = this.ruleConfigService.evaluatedFieldIds(rule);
		return evaluatees.every(e => {
			const field = find(fields, m => m.Id == e);
			if(field === undefined) {
				this.$logger.error(`Rule Id ${rule.Id} references a field that is not on the current form.  Removing rule.`, {
					rule, 
					invalidFieldId: e
				});
				return false;
			}
			return true;
		});
	};

	checkRuleForValidTriggers = (rule) => {
		if(!rule.Triggers?.length) {
			return true;
		}

		const validTriggerOperators = keyBy(Object.values(TRIGGER_OPERATOR_CODES));
		const hasValidTriggers = rule.Triggers.every(t => !!validTriggerOperators[t.Operator.Code]);
		if(!hasValidTriggers) {
			this.$logger.error(`Rule Id ${rule.Id} uses an invalid trigger operator.  Removing rule.`, { rule });
			return false;
		}

		return true;
	};

	validateRule = (rule, validators) => {
		let isValid = true;
		if(rule.Children) {
			isValid = rule.Children.every(c => this.validateRule(c, validators));
		}
		if(isValid) {
			return validators.every(v => v(rule)); 
		}
		return false;
	};


	filterToValidRules(rules = [], fields = []) {
		const validators = [
			this.checkRuleForPresenceOfChildrenOrEngine,
			this.checkRuleForValidEngineAndEvaluation,
			this.checkRuleForValidTriggers,
			this.checkRuleForValidFields(fields)
		];

		return rules.filter(r => this.validateRule(r, validators));
	}
}

export default angular
	.module("vesl.services.rule-validator", [])
	.service("ruleValidator", RuleValidator).name;
